<script lang="ts" setup>
import { dollars } from '@nuts/auto-delivery-sdk/dist/utils/money';
import { ref, toRef, watch } from 'vue';

import HeartIcon from '@/components/base/assets/HeartIcon.vue';
import UnstyledButton from '@/components/base/UnstyledButton.vue';
import { useCallback } from '@/composables/useCallback';
import { useLoginModal } from '@/composables/useLoginModal';
import { useProductDetail } from '@/composables/useProductDetail';
import money from '@/filters/money';
import { useCustomer } from '@/stores/customer';
import { gtag } from '@/utils/analytics';
import {
  getPrice,
  NutsProduct,
  NutsProductVariant,
  priceForQuantity,
  PurchaseOptions,
} from '@/utils/product';

const props = defineProps<{
  isFavorited?: boolean;
  product: NutsProduct;
  selectedVariant: NutsProductVariant;
  autoDeliverySelections?: PurchaseOptions['autoDelivery'];
}>();

const isFavoritedValue = ref(props.isFavorited);

watch([() => props.isFavorited, () => props.product.key], () => {
  isFavoritedValue.value = props.isFavorited;
});

const gtagEvent = () => {
  gtag('event', 'add_to_wishlist', {
    currency: 'USD',
    value: money(
      priceForQuantity(props.selectedVariant, {
        quantity: 1,
        autoDelivery: props.autoDeliverySelections,
      }),
    ),
    items: {
      item_id: props.product.key,
      item_name: props.product.name,
      item_variant: props.selectedVariant.sku,
      item_variant_name: props.selectedVariant.unitName,
      price: dollars(getPrice(props.selectedVariant).value),
      price_before_discount: dollars(
        priceForQuantity(props.selectedVariant, {
          quantity: 1,
        }),
      ),
      cost: dollars(props.selectedVariant.pieceCost),
      quantity: 1,
      weight: props.selectedVariant.weight,
    },
  });
};

const { handleOpen } = useLoginModal();
const { addToFavorites, removeFromFavorites } = useProductDetail(toRef(props, 'product'));

const addToFavoritesCallBack = useCallback(async () => {
  gtagEvent();
  isFavoritedValue.value = true;
  await addToFavorites();
});

const removeFromFavoritesCallBack = useCallback(async () => {
  isFavoritedValue.value = false;
  await removeFromFavorites();
});

const toggleFavorite = () => {
  if (!useCustomer().customer) {
    handleOpen({ callback: addToFavoritesCallBack });
    return;
  }

  if (!isFavoritedValue.value) {
    addToFavoritesCallBack.execute();
  } else {
    removeFromFavoritesCallBack.execute();
  }
};
</script>

<template>
  <UnstyledButton
    class="flex flex-row items-center text-sm bg-transparent cursor-pointer"
    data-test="favorite-button"
    data-promo="1"
    data-promo-creative="Add to favorite"
    data-promo-name="Favorite"
    @click="toggleFavorite"
  >
    <HeartIcon
      :alt="isFavoritedValue ? 'Heart filled - favorited' : 'Heart empty - not favorited'"
      aria-hidden="true"
      class="object-contain mr-3 text-nuts-red-800"
      :data-test="isFavoritedValue ? 'favorited-button-img' : 'not-favorited-button-img'"
      :size="16"
      :filled="isFavoritedValue"
    />
    <p class="mb-0 escape-legacy">
      {{ isFavoritedValue ? 'Added to favorites' : 'Add to favorites' }}
    </p>
  </UnstyledButton>
</template>
